import React from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { CheckCircle, Clock, User, MapPin, Eye } from 'lucide-react';

const ProfileList = ({ profiles, onOpen, statusColor, statusIcon, emptyMessage, search, setSearch, crewFilter, setCrewFilter }) => {
  const crews = React.useMemo(() => {
    const set = new Map();
    profiles.forEach(p => {
      const c = (p.luogo_crew || '').trim();
      if (c) {
        const k = c.toLowerCase();
        set.set(k, { label: c, count: (set.get(k)?.count || 0) + 1 });
      }
    });
    return Array.from(set.entries())
      .map(([k, v]) => ({ key: k, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [profiles]);

  const q = (search || '').trim().toLowerCase();
  const filtered = profiles.filter(p => {
    if (q) {
      const hay = `${p.name} ${p.nickname} ${p.luogo_crew || ''} ${p.instagram || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (crewFilter) {
      if ((p.luogo_crew || '').toLowerCase() !== crewFilter.toLowerCase()) return false;
    }
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="space-y-2 p-3 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="flex gap-2 items-center">
          <Input
            data-testid="freestyler-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca nome, nickname, crew, instagram..."
            className="bg-gray-900 border-gray-700 text-white text-sm"
          />
          <span className="text-gray-500 text-xs whitespace-nowrap">{filtered.length}/{profiles.length}</span>
        </div>
        {crews.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              data-testid="freestyler-crew-all"
              onClick={() => setCrewFilter('')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${
                !crewFilter ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}>Tutti</button>
            {crews.map(c => (
              <button
                key={c.key}
                data-testid={`freestyler-crew-${c.key}`}
                onClick={() => setCrewFilter(crewFilter === c.label ? '' : c.label)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${
                  crewFilter === c.label ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}>
                {c.label} ({c.count})
              </button>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
          <CheckCircle size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{q || crewFilter ? 'Nessun profilo corrisponde ai filtri' : emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((profile) => {
            const StatusIcon = statusIcon[profile.status] || Clock;
            return (
              <div key={profile.id}
                onClick={() => onOpen(profile)}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-800 hover:border-red-600 cursor-pointer transition-all duration-200 group"
                style={{ backgroundColor: 'var(--tp-dark)' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-gray-700 group-hover:border-red-500 transition-colors">
                  {profile.image ? (
                    <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--tp-gray)' }}>
                      <User size={18} className="text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-bold truncate" style={{ fontFamily: 'Oswald, sans-serif' }}>{profile.name}</p>
                    <span className="text-red-400 text-xs hidden sm:inline">"{profile.nickname}"</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 mt-0.5">
                    {profile.luogo_crew && (
                      <span className="text-gray-500 text-xs flex items-center gap-1">
                        <MapPin size={10} />{profile.luogo_crew}
                      </span>
                    )}
                    <span className="text-gray-600 text-[10px]">
                      {new Date(profile.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
                <Badge className={`${statusColor[profile.status]} text-white text-[10px] sm:text-xs gap-1 flex-shrink-0`}>
                  <StatusIcon size={10} />
                  <span className="hidden sm:inline">{profile.status === 'pending' ? 'In attesa' : profile.status === 'approved' ? 'Approvato' : 'Rifiutato'}</span>
                </Badge>
                {profile.status === 'pending' && (
                  <div className="hidden sm:flex gap-1 flex-shrink-0">
                    <Eye size={16} className="text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfileList;
